import React, { useState, useEffect, useRef } from "react";
import SiteWrapper from "../../SiteWrapper.react";
import { Card, Icon } from "tabler-react";
import { Crud_Catalogos } from "../../herramientas/Crud_Catalogos";
import Chart from "react-apexcharts";

const ESTATUS_COLORS = {
  alojados: "#467fcf",
  finalizados: "#5eba00",
  pendientes: "#f1c40f",
  en_ruta: "#17a2b8",
  no_facturable: "#cd201f",
  ov: "#6f42c1",
};

const POLL_INTERVAL = 60000; // 1 minuto

const DashboardFacturacionPage = () => {
  const [data, setData] = useState(null);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [filtroEstatus, setFiltroEstatus] = useState(null);
  const [filtroPlataforma, setFiltroPlataforma] = useState(null);
  const [filtroPais, setFiltroPais] = useState(null);
  const [filtroLocalidad, setFiltroLocalidad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monedaModoDinero, setMonedaModoDinero] = useState(false);
  const intervalRef = useRef(null);

  const buildUrl = (year, estatus, plataforma, pais, localidad) => {
    let url = "movimientos-facturar/dashboard";
    let params = [];
    if (year) params.push(`anio=${year}`);
    if (estatus) params.push(`estatus=${estatus}`);
    if (plataforma) params.push(`plataforma=${encodeURIComponent(plataforma)}`);
    if (pais) params.push(`pais=${encodeURIComponent(pais)}`);
    if (localidad) params.push(`localidad=${encodeURIComponent(localidad)}`);
    if (params.length) url += "?" + params.join("&");
    return url;
  };

  const fetchData = (year, estatus, plataforma, pais, localidad) => {
    const url = buildUrl(year, estatus, plataforma, pais, localidad);
    Crud_Catalogos(url, "dashboard", "get", "", [], "", "", "", [])
      .then((res) => { setData(res); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const refresh = (estatus = filtroEstatus, plataforma = filtroPlataforma, pais = filtroPais, localidad = filtroLocalidad, year = anio) => {
    clearInterval(intervalRef.current);
    fetchData(year, estatus, plataforma, pais, localidad);
    intervalRef.current = setInterval(() => fetchData(year, estatus, plataforma, pais, localidad), POLL_INTERVAL);
  };

  useEffect(() => {
    refresh();
    return () => clearInterval(intervalRef.current);
  }, []);

  const cambiarAnio = (nuevoAnio) => {
    setAnio(nuevoAnio);
    setFiltroEstatus(null);
    setFiltroPlataforma(null);
    setFiltroPais(null);
    setFiltroLocalidad(null);
    refresh(null, null, null, null, nuevoAnio);
  };

  const toggleFiltro = (estatus) => {
    const nuevo = (estatus === null || filtroEstatus === estatus) ? null : estatus;
    setFiltroEstatus(nuevo);
    refresh(nuevo, filtroPlataforma, filtroPais, filtroLocalidad, anio);
  };

  const togglePlataforma = (nombre) => {
    const nuevo = nombre === null ? null : (filtroPlataforma === nombre ? null : nombre);
    setFiltroPlataforma(nuevo);
    refresh(filtroEstatus, nuevo, filtroPais, filtroLocalidad, anio);
  };

  const seleccionarPlataforma = (nombre) => {
    const nuevo = nombre || null;
    setFiltroPlataforma(nuevo);
    refresh(filtroEstatus, nuevo, filtroPais, filtroLocalidad, anio);
  };

  const togglePais = (nombre) => {
    const nuevo = filtroPais === nombre ? null : nombre;
    setFiltroPais(nuevo);
    refresh(filtroEstatus, filtroPlataforma, nuevo, filtroLocalidad, anio);
  };

  const toggleLocalidad = (nombre) => {
    const nuevo = filtroLocalidad === nombre ? null : nombre;
    setFiltroLocalidad(nuevo);
    refresh(filtroEstatus, filtroPlataforma, filtroPais, nuevo, anio);
  };

  if (loading && !data) {
    return (
      <SiteWrapper>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
          <div className="spinner-border text-primary"></div>
        </div>
      </SiteWrapper>
    );
  }

  const kpis = data?.kpis || {};
  const total = Number(kpis.total) || 1;

  const plataformaData = (data?.porPlataforma || []).filter(p => Number(p.total) > 0);
  const paisData = (data?.porPais || []).filter(p => Number(p.total) > 0);
  const localidadData = (data?.porLocalidad || []).filter(l => Number(l.total) > 0);
  const activeColor = filtroEstatus ? ESTATUS_COLORS[filtroEstatus] : "#467fcf";

  return (
    <SiteWrapper>
      <div style={{ padding: "1rem 1.5rem" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ margin: 0 }}><Icon name="bar-chart-2" className="mr-2" />Panel de Validación</h3>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <select className="form-control form-control-sm" value={anio} onChange={e => cambiarAnio(parseInt(e.target.value))} style={{ width: "100px" }}>
              {[...Array(5)].map((_, i) => {
                const y = new Date().getFullYear() - i;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
            <span className="badge badge-secondary" style={{ fontSize: "10px" }}>
              <i className="fe fe-refresh-cw mr-1"></i>Auto: 1 min
            </span>
            {(filtroEstatus || filtroPlataforma || filtroPais || filtroLocalidad) && (
              <button className="btn btn-sm btn-outline-danger" onClick={() => { setFiltroEstatus(null); setFiltroPlataforma(null); setFiltroPais(null); setFiltroLocalidad(null); refresh(null, null, null, null, anio); }}>
                <i className="fe fe-x mr-1"></i>Limpiar filtros
              </button>
            )}
          </div>
        </div>

        {/* KPI Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "12px", marginBottom: "1.5rem" }}>
          <KpiCard label="Total Servicios" value={kpis.total} color="#6c757d" active={!filtroEstatus} onClick={() => toggleFiltro(null)} />
          <KpiCard label="Alojados" value={kpis.alojados} pct={((kpis.alojados / total) * 100).toFixed(0)} color={ESTATUS_COLORS.alojados} active={filtroEstatus === "alojados"} onClick={() => toggleFiltro("alojados")} />
          <KpiCard label="Finalizados" value={kpis.finalizados} pct={((kpis.finalizados / total) * 100).toFixed(0)} color={ESTATUS_COLORS.finalizados} active={filtroEstatus === "finalizados"} onClick={() => toggleFiltro("finalizados")} />
          <KpiCard label="Pendientes" value={kpis.pendientes} pct={((kpis.pendientes / total) * 100).toFixed(0)} color={ESTATUS_COLORS.pendientes} active={filtroEstatus === "pendientes"} onClick={() => toggleFiltro("pendientes")} />
          <KpiCard label="En Ruta" value={kpis.en_ruta} pct={((kpis.en_ruta / total) * 100).toFixed(0)} color={ESTATUS_COLORS.en_ruta} active={filtroEstatus === "en_ruta"} onClick={() => toggleFiltro("en_ruta")} />
          <KpiCard label="No Facturable" value={kpis.no_facturable} pct={((kpis.no_facturable / total) * 100).toFixed(0)} color={ESTATUS_COLORS.no_facturable} active={filtroEstatus === "no_facturable"} onClick={() => toggleFiltro("no_facturable")} />
          <KpiCard label="OV" value={kpis.ov} pct={((kpis.ov / total) * 100).toFixed(0)} color={ESTATUS_COLORS.ov} active={filtroEstatus === "ov"} onClick={() => toggleFiltro("ov")} />
        </div>

        {/* Charts Row 1: Plataforma + Moneda */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
          {/* Barras Apiladas Verticales - Clientes/Plataforma */}
          <Card>
            <Card.Header>
              <Card.Title>Por Plataforma (Cliente)</Card.Title>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
                {filtroPlataforma && (
                  <span className="badge badge-primary" style={{ cursor: "pointer" }} onClick={() => togglePlataforma(filtroPlataforma)}>
                    {filtroPlataforma} ✕
                  </span>
                )}
                <select
                  className="form-control form-control-sm"
                  value={filtroPlataforma || ""}
                  onChange={e => seleccionarPlataforma(e.target.value)}
                  style={{ width: "180px" }}
                >
                  <option value="">Todas las plataformas</option>
                  {(data?.plataformas || []).map(p => (
                    <option key={p.nombre} value={p.nombre}>{p.nombre}</option>
                  ))}
                </select>
              </div>
            </Card.Header>
            <Card.Body>
              <Chart
                type="bar"
                height={350}
                options={{
                  chart: { stacked: true, toolbar: { show: false }, events: { dataPointSelection: (e, chart, opts) => { togglePlataforma(plataformaData[opts.dataPointIndex]?.plataforma); } } },
                  xaxis: { categories: plataformaData.map(p => p.plataforma || "Sin nombre"), labels: { style: { cursor: "pointer" } } },
                  colors: filtroEstatus ? [ESTATUS_COLORS[filtroEstatus]] : [ESTATUS_COLORS.alojados, ESTATUS_COLORS.finalizados, ESTATUS_COLORS.pendientes, ESTATUS_COLORS.en_ruta, ESTATUS_COLORS.no_facturable, ESTATUS_COLORS.ov],
                  plotOptions: { bar: { horizontal: false, columnWidth: "60%", borderRadius: 4 } },
                  dataLabels: { enabled: false },
                  tooltip: { shared: true, intersect: false },
                  legend: { position: "top", show: !filtroEstatus },
                  states: { active: { filter: { type: "darken", value: 0.7 } } },
                }}
                series={filtroEstatus
                  ? [{ name: filtroEstatus, data: plataformaData.map(p => Number(p[filtroEstatus]) || 0) }]
                  : [
                    { name: "Alojados", data: plataformaData.map(p => Number(p.alojados) || 0) },
                    { name: "Finalizados", data: plataformaData.map(p => Number(p.finalizados) || 0) },
                    { name: "Pendientes", data: plataformaData.map(p => Number(p.pendientes) || 0) },
                    { name: "En ruta", data: plataformaData.map(p => Number(p.en_ruta) || 0) },
                    { name: "No facturable", data: plataformaData.map(p => Number(p.no_facturable) || 0) },
                    { name: "OV", data: plataformaData.map(p => Number(p.ov) || 0) },
                  ]
                }
              />
            </Card.Body>
          </Card>

          {/* Donut Charts - Moneda */}
          <Card>
            <Card.Header>
              <Card.Title>Por Moneda ({monedaModoDinero ? "Dinero" : "Servicios"})</Card.Title>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "12px", color: !monedaModoDinero ? "#467fcf" : "#868e96", fontWeight: !monedaModoDinero ? 600 : 400 }}>Servicios</span>
                <label style={{ position: "relative", display: "inline-block", width: "40px", height: "20px", margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={monedaModoDinero}
                    onChange={() => setMonedaModoDinero(!monedaModoDinero)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: "absolute", cursor: "pointer", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: monedaModoDinero ? "#5eba00" : "#ccc",
                    borderRadius: "20px", transition: "0.3s",
                  }}>
                    <span style={{
                      position: "absolute", content: "''", height: "14px", width: "14px",
                      left: monedaModoDinero ? "22px" : "3px", bottom: "3px",
                      backgroundColor: "#fff", borderRadius: "50%", transition: "0.3s",
                    }}></span>
                  </span>
                </label>
                <span style={{ fontSize: "12px", color: monedaModoDinero ? "#5eba00" : "#868e96", fontWeight: monedaModoDinero ? 600 : 400 }}>Dinero</span>
              </div>
            </Card.Header>
            <Card.Body>
              <div style={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: "16px" }}>
                {(data?.porMoneda || []).filter(m => m.moneda === "USD" || m.moneda === "MXN").map(m => {
                  const series = monedaModoDinero
                    ? (filtroEstatus
                      ? [Number(m[`monto_${filtroEstatus}`]) || 0]
                      : [Number(m.monto_alojados) || 0, Number(m.monto_finalizados) || 0, Number(m.monto_pendientes) || 0, Number(m.monto_en_ruta) || 0, Number(m.monto_no_facturable) || 0, Number(m.monto_ov) || 0])
                    : (filtroEstatus
                      ? [Number(m[filtroEstatus]) || 0]
                      : [Number(m.alojados) || 0, Number(m.finalizados) || 0, Number(m.pendientes) || 0, Number(m.en_ruta) || 0, Number(m.no_facturable) || 0, Number(m.ov) || 0]);
                  const labels = filtroEstatus
                    ? [filtroEstatus.charAt(0).toUpperCase() + filtroEstatus.slice(1).replace("_", " ")]
                    : ["Alojados", "Finalizados", "Pendientes", "En ruta", "No facturable", "OV"];
                  const colors = filtroEstatus
                    ? [ESTATUS_COLORS[filtroEstatus]]
                    : [ESTATUS_COLORS.alojados, ESTATUS_COLORS.finalizados, ESTATUS_COLORS.pendientes, ESTATUS_COLORS.en_ruta, ESTATUS_COLORS.no_facturable, ESTATUS_COLORS.ov];
                  const totalMoneda = series.reduce((a, b) => a + b, 0);
                  if (totalMoneda === 0) return null;
                  return (
                    <div key={m.moneda} style={{ textAlign: "center", flex: "1", minWidth: "220px" }}>
                      <h5 style={{ marginBottom: "4px" }}>{m.moneda}</h5>
                      <span style={{ fontSize: "12px", color: "#868e96" }}>
                        {monedaModoDinero
                          ? `$${totalMoneda.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : `${totalMoneda.toLocaleString("en-US")} servicios`
                        }
                      </span>
                      <Chart
                        type="donut"
                        height={280}
                        options={{
                          labels: labels,
                          colors: colors,
                          legend: { position: "bottom", fontSize: "12px" },
                          dataLabels: { enabled: true, formatter: monedaModoDinero
                            ? (val, opts) => `$${opts.w.config.series[opts.seriesIndex].toLocaleString("en-US", { maximumFractionDigits: 0 })}`
                            : (val) => val.toFixed(0) + "%"
                          },
                          plotOptions: { pie: { donut: { size: "55%" } } },
                          tooltip: monedaModoDinero ? {
                            y: { formatter: (val) => `$${val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
                          } : {},
                        }}
                        series={series}
                      />
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Charts Row 2: País (barras horizontales apiladas) + Localidad (barras horizontales apiladas) */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
          <Card>
            <Card.Header>
              <Card.Title>Por País</Card.Title>
              {filtroPais && <span className="badge badge-info ml-2">{filtroPais} ✕</span>}
            </Card.Header>
            <Card.Body>
              <Chart
                type="bar"
                height={320}
                options={{
                  chart: { stacked: true, toolbar: { show: false }, events: { dataPointSelection: (e, chart, opts) => { togglePais(paisData[opts.dataPointIndex]?.pais); } } },
                  xaxis: { categories: paisData.map(p => p.pais) },
                  colors: filtroEstatus ? [ESTATUS_COLORS[filtroEstatus]] : [ESTATUS_COLORS.alojados, ESTATUS_COLORS.finalizados, ESTATUS_COLORS.pendientes, ESTATUS_COLORS.en_ruta, ESTATUS_COLORS.no_facturable, ESTATUS_COLORS.ov],
                  plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
                  dataLabels: { enabled: false },
                  tooltip: { shared: true, intersect: false },
                  legend: { position: "top", show: !filtroEstatus },
                  states: { active: { filter: { type: "darken", value: 0.7 } } },
                }}
                series={filtroEstatus
                  ? [{ name: filtroEstatus, data: paisData.map(p => Number(p[filtroEstatus]) || 0) }]
                  : [
                    { name: "Alojados", data: paisData.map(p => Number(p.alojados) || 0) },
                    { name: "Finalizados", data: paisData.map(p => Number(p.finalizados) || 0) },
                    { name: "Pendientes", data: paisData.map(p => Number(p.pendientes) || 0) },
                    { name: "En ruta", data: paisData.map(p => Number(p.en_ruta) || 0) },
                    { name: "No facturable", data: paisData.map(p => Number(p.no_facturable) || 0) },
                    { name: "OV", data: paisData.map(p => Number(p.ov) || 0) },
                  ]
                }
              />
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title>Por Localidad (Top 10)</Card.Title>
              {filtroLocalidad && <span className="badge badge-success ml-2">{filtroLocalidad} ✕</span>}
            </Card.Header>
            <Card.Body>
              <Chart
                type="bar"
                height={320}
                options={{
                  chart: { stacked: true, toolbar: { show: false }, events: { dataPointSelection: (e, chart, opts) => { toggleLocalidad(localidadData[opts.dataPointIndex]?.localidad); } } },
                  xaxis: { categories: localidadData.map(l => l.localidad || "Sin nombre") },
                  colors: filtroEstatus ? [ESTATUS_COLORS[filtroEstatus]] : [ESTATUS_COLORS.alojados, ESTATUS_COLORS.finalizados, ESTATUS_COLORS.pendientes, ESTATUS_COLORS.en_ruta, ESTATUS_COLORS.no_facturable, ESTATUS_COLORS.ov],
                  plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
                  dataLabels: { enabled: false },
                  tooltip: { shared: true, intersect: false },
                  legend: { position: "top", show: !filtroEstatus },
                  states: { active: { filter: { type: "darken", value: 0.7 } } },
                }}
                series={filtroEstatus
                  ? [{ name: filtroEstatus, data: localidadData.map(l => Number(l[filtroEstatus]) || 0) }]
                  : [
                    { name: "Alojados", data: localidadData.map(l => Number(l.alojados) || 0) },
                    { name: "Finalizados", data: localidadData.map(l => Number(l.finalizados) || 0) },
                    { name: "Pendientes", data: localidadData.map(l => Number(l.pendientes) || 0) },
                    { name: "En ruta", data: localidadData.map(l => Number(l.en_ruta) || 0) },
                    { name: "No facturable", data: localidadData.map(l => Number(l.no_facturable) || 0) },
                    { name: "OV", data: localidadData.map(l => Number(l.ov) || 0) },
                  ]
                }
              />
            </Card.Body>
          </Card>
        </div>

        {/* Charts Row 3: Mes (áreas apiladas) + Semana (líneas) */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
          {/* Áreas Apiladas - Por Mes */}
          <Card>
            <Card.Header><Card.Title>Por Mes</Card.Title></Card.Header>
            <Card.Body>
              <Chart
                type="area"
                height={320}
                options={{
                  chart: { stacked: true, toolbar: { show: false } },
                  xaxis: { categories: (data?.porMes || []).map(m => m.mes) },
                  colors: filtroEstatus ? [ESTATUS_COLORS[filtroEstatus]] : [ESTATUS_COLORS.alojados, ESTATUS_COLORS.finalizados, ESTATUS_COLORS.pendientes, ESTATUS_COLORS.en_ruta, ESTATUS_COLORS.no_facturable, ESTATUS_COLORS.ov],
                  stroke: { curve: "smooth", width: 2 },
                  fill: { type: "gradient", gradient: { opacityFrom: 0.6, opacityTo: 0.1 } },
                  dataLabels: { enabled: false },
                  tooltip: { shared: true, intersect: false },
                  legend: { position: "top", show: !filtroEstatus },
                }}
                series={filtroEstatus
                  ? [{ name: filtroEstatus, data: (data?.porMes || []).map(m => Number(m[filtroEstatus]) || 0) }]
                  : [
                    { name: "Alojados", data: (data?.porMes || []).map(m => Number(m.alojados) || 0) },
                    { name: "Finalizados", data: (data?.porMes || []).map(m => Number(m.finalizados) || 0) },
                    { name: "Pendientes", data: (data?.porMes || []).map(m => Number(m.pendientes) || 0) },
                    { name: "En ruta", data: (data?.porMes || []).map(m => Number(m.en_ruta) || 0) },
                    { name: "No facturable", data: (data?.porMes || []).map(m => Number(m.no_facturable) || 0) },
                    { name: "OV", data: (data?.porMes || []).map(m => Number(m.ov) || 0) },
                  ]
                }
              />
            </Card.Body>
          </Card>

          {/* Líneas - Por Semana */}
          <Card>
            <Card.Header><Card.Title>Por Semana del Año</Card.Title></Card.Header>
            <Card.Body>
              <Chart
                type="line"
                height={320}
                options={{
                  chart: { toolbar: { show: false } },
                  xaxis: { categories: (data?.porSemana || []).map(s => "S" + s.semana) },
                  colors: filtroEstatus ? [ESTATUS_COLORS[filtroEstatus]] : [ESTATUS_COLORS.alojados, ESTATUS_COLORS.finalizados, ESTATUS_COLORS.pendientes, ESTATUS_COLORS.en_ruta, ESTATUS_COLORS.no_facturable, ESTATUS_COLORS.ov],
                  stroke: { curve: "smooth", width: 3 },
                  markers: { size: 4 },
                  dataLabels: { enabled: false },
                  tooltip: { shared: true, intersect: false },
                  legend: { position: "top", show: !filtroEstatus },
                }}
                series={filtroEstatus
                  ? [{ name: filtroEstatus, data: (data?.porSemana || []).map(s => Number(s[filtroEstatus]) || 0) }]
                  : [
                    { name: "Alojados", data: (data?.porSemana || []).map(s => Number(s.alojados) || 0) },
                    { name: "Finalizados", data: (data?.porSemana || []).map(s => Number(s.finalizados) || 0) },
                    { name: "Pendientes", data: (data?.porSemana || []).map(s => Number(s.pendientes) || 0) },
                    { name: "En ruta", data: (data?.porSemana || []).map(s => Number(s.en_ruta) || 0) },
                    { name: "No facturable", data: (data?.porSemana || []).map(s => Number(s.no_facturable) || 0) },
                    { name: "OV", data: (data?.porSemana || []).map(s => Number(s.ov) || 0) },
                  ]
                }
              />
            </Card.Body>
          </Card>
        </div>
      </div>
    </SiteWrapper>
  );
};

const KpiCard = ({ label, value, pct, color, active, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: active ? color : "#fff",
      color: active ? "#fff" : "#333",
      border: `2px solid ${color}`,
      borderRadius: "8px",
      padding: "16px",
      cursor: "pointer",
      transition: "all 0.2s",
      textAlign: "center",
      boxShadow: active ? `0 4px 12px ${color}44` : "0 1px 3px rgba(0,0,0,0.08)",
    }}
  >
    <div style={{ fontSize: "24px", fontWeight: 700 }}>{Number(value || 0).toLocaleString("en-US")}</div>
    <div style={{ fontSize: "12px", opacity: 0.85, marginTop: "4px" }}>{label}</div>
    {pct && <div style={{ fontSize: "11px", opacity: 0.7, marginTop: "2px" }}>{pct}%</div>}
  </div>
);

export default DashboardFacturacionPage;
